import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import { Checkbox } from "../ui/checkbox";
import { GroupedTableFilters } from "./GroupedTableFilters";

interface GroupedTableTopbarProps<TSource extends object> {
  selectedItems: Map<string, TSource>;
  isSearchable?: boolean;
  checkedActions?: (selectedItems: Map<string, TSource>) => React.ReactNode;
  onSelectAll: () => void;
  isAllSelected: boolean;
  searchAndFilters?: {
    searchText: string;
    setSearchText: (text: string) => void;
    groupLabels: string[];
    selectedGroups: Set<string>;
    updateGroupFilter: (groupLabel: string, checked: boolean) => void;
    clearAllFilters: () => void;
    hasActiveFilters: boolean;
    activeFilterCount: number;
  };
}

export function GroupedTableTopbar<TSource extends object>({
  selectedItems,
  isSearchable = false,
  checkedActions,
  onSelectAll,
  isAllSelected,
  searchAndFilters,
}: GroupedTableTopbarProps<TSource>) {
  const [isEntering, setIsEntering] = React.useState(false);

  const isTriggeredBySelection = selectedItems.size > 0;
  const shouldShowTopbar = isTriggeredBySelection || isSearchable;

  const topbarContent = (
    <div
      className={cn(
        "flex h-12 flex-row items-center justify-between gap-4 px-2 text-sm",
        {
          "bg-accent-3": isTriggeredBySelection && isEntering,
        },
      )}
    >
      {checkedActions && (
        <div
          className={cn("flex flex-row items-center gap-2", {
            "min-w-[120px]": isSearchable,
          })}
        >
          <Checkbox
            checked={isAllSelected}
            onCheckedChange={onSelectAll}
            title={isAllSelected ? "Deselect All" : "Select All"}
            aria-label={isAllSelected ? "Deselect All" : "Select All"}
          />
          {selectedItems.size > 0 ? (
            <div>{selectedItems.size} selected</div>
          ) : (
            <div>Select All</div>
          )}
        </div>
      )}
      {isSearchable && searchAndFilters && (
        <div
          className={cn("mr-auto flex flex-row items-center gap-2", {
            "ml-4": checkedActions,
          })}
        >
          <GroupedTableFilters
            searchText={searchAndFilters.searchText}
            onSearchChange={searchAndFilters.setSearchText}
            groupLabels={searchAndFilters.groupLabels}
            selectedGroups={searchAndFilters.selectedGroups}
            onGroupFilterChange={searchAndFilters.updateGroupFilter}
            onClearAllFilters={searchAndFilters.clearAllFilters}
            hasActiveFilters={searchAndFilters.hasActiveFilters}
            activeFilterCount={searchAndFilters.activeFilterCount}
          />
        </div>
      )}
      <div className="flex flex-row items-center gap-2">
        {checkedActions?.(selectedItems)}
      </div>
    </div>
  );

  if (!shouldShowTopbar) {
    return null;
  }

  if (isSearchable) {
    return topbarContent;
  }

  return (
    <AnimatePresence>
      {isTriggeredBySelection && (
        <motion.div
          key="selected-items-bar"
          initial={{ opacity: 0, transform: "translateY(-20px)", height: 0 }}
          animate={{ height: "48px", opacity: 1, transform: "translateY(0)" }}
          exit={{ opacity: 0, transform: "translateY(-20px)", height: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          onAnimationStart={() => setIsEntering(true)}
          onAnimationComplete={() => setIsEntering(false)}
        >
          {topbarContent}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
